import { joiResolver } from '@hookform/resolvers/joi';
import { Box, Checkbox, Dialog, Flex, Text } from '@radix-ui/themes';
import Joi from 'joi';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import ComboBox from '~/components/Combobox';
import Form from '~/components/Form';
import FancyUnitInput from '~/components/Input/Elements/FancyUnitInput';
import { StyledTextFieldRoot } from '~/components/Input/Elements/TextField';
import InputFieldWrapper from '~/components/InputFieldWrapper';
import LoadingOverlay from '~/components/LoadingOverlay';
import TagsSelector from '~/containers/TagsSelector';
import Extras from './Extras';
import { extrasDefinitionType, formDefinitionType } from './types';

function generateJoiSchema(formDefinition) {
  const schema = {};
  formDefinition.forEach(({ valueKey, label, validation }) => {
    schema[valueKey] = validation?.label(label) || Joi.any().label(label);
  });
  return Joi.object(schema);
}

function getDefaultValues(schema, model) {
  const { value } = schema.validate(model, {
    stripUnknown: true,
  });
  return value;
}

function ModelDialog({
  formDefinition,
  extrasDefinition,
  model,
  extras,
  loading,
  isOpen,
  onDialogClose,
  title,
  onClickSubmit,
  showConfirm,
  ConfirmDialogContent,
}) {
  const schema = useMemo(() => generateJoiSchema(formDefinition), [formDefinition]);
  const defaultValues = useMemo(() => getDefaultValues(schema, model), [schema, model]);

  const {
    handleSubmit,
    register,
    setValue,
    unregister,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: joiResolver(generateJoiSchema(formDefinition)),
    mode: 'onChange',
  });

  const values = watch();

  const EditContent = useMemo(
    () => (
      <Flex
        direction='column'
        gap='4'
      >
        {formDefinition.map((props, idx) => {
          const dynamicProps = props.getDynamicProps ? props.getDynamicProps({ model, values }) : {};
          const {
            valueKey,
            type,
            show = true,
            label,
            helpText,
            maxVisibleTags = 2,
            ...rest
          } = { ...props, ...dynamicProps };

          if (!show) {
            if (values[valueKey] !== undefined) unregister(valueKey);
            return null;
          }

          if (type === 'checkbox') {
            return (
              <Text
                key={idx}
                as='label'
                size='2'
              >
                <Flex gap='2'>
                  <Checkbox
                    onCheckedChange={checked => setValue(valueKey, checked)}
                    defaultChecked={values[valueKey]}
                  />
                  {label}
                </Flex>
              </Text>
            );
          }

          return (
            <InputFieldWrapper
              key={idx}
              label={label}
              helpText={helpText}
              errorText={errors[valueKey]?.message}
            >
              {type === 'combobox' && (
                <ComboBox
                  defaultSelected={values[valueKey]}
                  onSelectedChange={value => {
                    setValue(valueKey, value?.value);
                  }}
                  error={errors[valueKey]?.message}
                  {...rest}
                />
              )}
              {type === 'tags-select' && (
                <TagsSelector
                  maxVisibleTags={maxVisibleTags}
                  defaultSelected={values[valueKey]}
                  onChange={value => setValue(valueKey, value)}
                  error={errors[valueKey]?.message}
                  {...rest}
                />
              )}
              {type === 'euro' && (
                <FancyUnitInput
                  {...register(valueKey)}
                  {...rest}
                  minAmount={0}
                />
              )}
              {type === 'text' && (
                <StyledTextFieldRoot
                  {...register(valueKey)}
                  type='text'
                  error={errors[valueKey]?.message}
                  {...rest}
                />
              )}
              {type === 'number' && (
                <StyledTextFieldRoot
                  {...register(valueKey)}
                  type='number'
                  min='0'
                  error={errors[valueKey]?.message}
                  {...rest}
                />
              )}
            </InputFieldWrapper>
          );
        })}
        <Extras
          definition={extrasDefinition}
          extras={extras}
        />
        {loading && <LoadingOverlay />}
        {showConfirm && <ConfirmDialogContent data={watch()} />}
      </Flex>
    ),
    [
      formDefinition,
      extrasDefinition,
      extras,
      loading,
      showConfirm,
      values,
      errors,
      watch,
      model,
      register,
      unregister,
      setValue,
    ],
  );

  function onOpenChange(open) {
    if (!open) onDialogClose();
  }

  function handleOnClickSubmit(data) {
    onClickSubmit(data, false);
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog.Content maxWidth='448px'>
        <Dialog.Title>{title}</Dialog.Title>
        <Form
          buttonGroupMargin={{ top: '2' }}
          onClickSubmit={data => handleSubmit(handleOnClickSubmit)(data)}
          onClickCancel={onDialogClose}
        >
          <Flex
            direction='column'
            gap='4'
            width='100%'
          >
            <Box
              maxHeight='400px'
              width='100%'
              asChild
            >
              {EditContent}
            </Box>
          </Flex>
        </Form>
      </Dialog.Content>
    </Dialog.Root>
  );
}

ModelDialog.propTypes = {
  formDefinition: formDefinitionType,
  extrasDefinition: extrasDefinitionType,
  model: PropTypes.object.isRequired,
  extras: PropTypes.object,
  error: PropTypes.object,
  loading: PropTypes.bool,
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onDialogClose: PropTypes.func.isRequired,
  onClickSubmit: PropTypes.func.isRequired,
  showConfirm: PropTypes.bool,
  ConfirmDialogContent: PropTypes.func,
  getDynamicProps: PropTypes.func,
};

export default ModelDialog;
